import {
  Abi,
  Address,
  ContractFunctionName as ViemContractFunctionName,
  encodeFunctionData,
  EncodeFunctionDataParameters,
} from "viem"
import { useEstimateGas, UseEstimateGasParameters, useGasPrice } from "wagmi"

import {
  ONE_MINUTE_IN_SECONDS,
  ONE_SECOND_IN_MILLISECONDS,
} from "../../constants"

export default function useEstimateTransactionGas<
  const ContractAbi extends Abi | readonly unknown[],
  ContractFunctionName extends
    | ViemContractFunctionName<ContractAbi>
    | undefined = undefined,
>(
  contract: EncodeFunctionDataParameters<ContractAbi, ContractFunctionName> & {
    address: Address
  },
  options: {
    chainId: number
    query: UseEstimateGasParameters["query"]
  },
) {
  const queryOptions = options.query ?? {}

  const {
    data: gasAmount,
    isFetching: isFetchingGasAmount,
    error,
  } = useEstimateGas({
    to: contract.address,
    data: encodeFunctionData(contract as EncodeFunctionDataParameters),
    chainId: options.chainId,
    query: options.query,
  })

  const { data: gasPrice, isFetching: isFetchingGasPrice } = useGasPrice({
    chainId: options.chainId,
    query: {
      enabled: !!gasAmount && !!queryOptions.enabled,
      refetchInterval: ONE_MINUTE_IN_SECONDS * ONE_SECOND_IN_MILLISECONDS,
      staleTime: ONE_MINUTE_IN_SECONDS * ONE_SECOND_IN_MILLISECONDS,
    },
  })

  const estimatedTransactionFee = (gasAmount ?? 0n) * (gasPrice ?? 0n)

  return {
    isFetching: isFetchingGasAmount || isFetchingGasPrice,
    gas: gasAmount,
    gasPrice,
    estimatedTransactionFee,
    error,
  }
}
